'use client'

import {
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Stack,
  Button,
  InputGroup,
  InputLeftElement,
  Text,
  useColorModeValue,
  Link,
  Image,
} from '@chakra-ui/react'
import { PasswordField } from '../../components/PasswordField';
import { ToastService } from '../../services/toast/toastService';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useRegisterMutation } from "../../slices/userApiSlice";
import { setCredentials } from "../../slices/authSlice";
import { useUserSendEmailOtpMutation, useUserVerifyEmailMutation } from "../../slices/userApiSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAt, faUser } from "@fortawesome/free-solid-svg-icons"; 

export default function UserRegister() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmpassword, setConfirmPassword] = useState('');
  const [password, setPassword] = useState('');
  const {success, error} = ToastService();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  //const bgColor = useColorModeValue('custom.900', 'gray.800');
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('custom.800', 'custom.900');
  const { userInfo } = useSelector( (state) => state.auth );
  const [register, { isLoading }] = useRegisterMutation();
  const [formErrors, setFormErrors] = useState();
  const [otp, setOTP] = useState(""); 
  const [resendDisabled, setResendDisabled] = useState(false); 
  const [resendTimer, setResendTimer] = useState(5 * 60); // 10 minutes timer
  const [expirationTime, setExpirationTime] = useState(null); // Store expiration time
  const [sendEmailOtp] = useUserSendEmailOtpMutation(); // Send OTP hook
  const [verifyEmail] = useUserVerifyEmailMutation(); // Verify OTP hook
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  useEffect(() => {
    if (userInfo) {
      navigate("/");
    }
  
    // Load reCAPTCHA v3 script
    const loadRecaptcha = () => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setRecaptchaLoaded(true);
      };
      document.body.appendChild(script);
    };
  
    if (!window.grecaptcha) {
      loadRecaptcha();
    } else {
      setRecaptchaLoaded(true);
    }
  
    return () => {
      const script = document.querySelector(`script[src^="https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}"]`);
      if (script) document.body.removeChild(script);
    };
  }, [navigate, userInfo]);

  const getRecaptchaToken = async (action) => {
    try {
      // Wait for grecaptcha to be loaded
      await new Promise((resolve) => {
        const checkRecaptcha = () => {
          if (window.grecaptcha && window.grecaptcha.execute) {
            resolve();
          } else {
            setTimeout(checkRecaptcha, 100);
          }
        };
        checkRecaptcha();
      });

      const token = await window.grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY, 
        { action }
      );
      return token;
    } catch (err) {
      console.error('reCAPTCHA error:', err);
      //throw new Error('Failed to verify reCAPTCHA');
    }
  };

  const validateForm = (excludeOTP = false) => {
    let errors = {};
    if (!name) {
      errors.name = "Please enter your display name.";
    }
    if (!email) {
      errors.email = "Please enter your email address.";
    }
    if (!password) {
      errors.password = "Please enter your password.";
    }
    if (!confirmpassword) {
      errors.confirmpassword = "Please confirm your password.";
    }
    if (password && confirmpassword && password !== confirmpassword) {
      errors.confirmpassword = "Your passwords don't match. Please try again.";
    }
    if (!excludeOTP && !otp) {
      errors.otp = "Verification code is required.";
    }
    return errors;
  };

  const sendOTP = async (e) => {
    // Validate form
    const validationErrors = validateForm(true);
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        ...validationErrors,
      }));
      return;
    }
  
    try {
      const token = await getRecaptchaToken('sendOTP');
      const response = await sendEmailOtp({ email, recaptchaToken: token }).unwrap();
  
      if (response.success) {
        // Set new expiration time (5 minutes from now)
        const newExpirationTime = Date.now() + 5 * 60 * 1000; // 5 minutes in milliseconds
        setExpirationTime(newExpirationTime);
  
        // Reset resend timer and disable the resend button
        setResendDisabled(true);
        setResendTimer(5 * 60); // Reset timer to 5 minutes
  
        success('A verification code has been sent to your email.');
      } else {
        error('Please check your email address');
      }
    } catch (err) {
      error(err.data?.message || 'Failed to send OTP. Please try again.');
    }
  };

  useEffect(() => {
    if (expirationTime) {
      const interval = setInterval(() => {
        const remainingTime = Math.max(0, Math.floor((expirationTime - Date.now()) / 1000)); // Calculate remaining time in seconds
        setResendTimer(remainingTime);

        if (remainingTime <= 0) {
          setResendDisabled(false); // Re-enable resend button after OTP expiration
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval); // Cleanup interval on component unmount
    }
  }, [expirationTime]);

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60); // Get minutes
    const seconds = timeInSeconds % 60; // Get remaining seconds
    return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`; // Format as MM:SS
  };

  const submitHandler = async (e) => {
    e.preventDefault();
  
    // Step 1: Validate the form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        ...validationErrors,
      }));
      return; // Exit if validation fails
    }
  
    // Step 2: Check if OTP has expired (only if expirationTime is set)
    if (expirationTime && Date.now() > expirationTime) {
      setFormErrors('OTP has expired. Please request a new OTP.');
      return; // Exit if OTP has expired
    }
  
    try {

      if (!recaptchaLoaded) {
        error("Security verification is still loading. Please try again.");
        return;
      }
      
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken('verify_email');
      if (!recaptchaToken) {
        error("Security verification failed. Please try again.");
        return;
      }

      // Step 3: Verify OTP - Now properly passing recaptchaToken
      const verifyResponse = await verifyEmail({ 
        email, 
        otp, 
        expirationTime, 
        recaptchaToken: recaptchaToken
      }).unwrap();

      if (!verifyResponse.success) {
        setFormErrors(verifyResponse.message || 'Email verification failed');
        return;
      }
      
      const token = await getRecaptchaToken('register');
      if (!token) {
        error("Security verification failed. Please try again.");
        return;
      }

      // Step 4: Proceed with registration
      const registerResponse = await register({ 
        name, 
        email, 
        password, 
        recaptchaToken: token 
      }).unwrap();

      const { qrCode, user } = registerResponse;
      // Step 5: Dispatch user credentials to Redux store
      dispatch(setCredentials({ ...user, qrCode }));
  
      // Step 6: Clear form after successful registration
      setName('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
  
      navigate('/'); // Navigate to the home page after registration
      success('Registration successful!');
    } catch (err) {
      // Handle any errors that occur during verification or registration
      const errorMessage = err?.data?.message || 'An unexpected error occurred.';
      setFormErrors(errorMessage);
      error(errorMessage);
    }
  };

  // OTP input change handler
  const handleOTPChange = (e) => {
    const value = e.target.value;
  
    // Update name state
    setOTP(value);
  
    // Clear error message if input value changes
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      otp: "", // Clear the error for the name field
    }));

  };

  const handleNameChange = (e) => {
    const value = e.target.value;
  
    // Update name state
    setName(value);
  
    // Clear error message if input value changes
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      name: "", // Clear the error for the name field
    }));
  };
  

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
  };  

  const handleEmailBlur = () => {
    if (!email) return; // If email is empty, do nothing
  
    // Email domain check (Only allow Gmail)
    const emailDomain = email.split('@')[1];
    if (emailDomain && emailDomain !== 'gmail.com') {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        email: 'Only Gmail addresses are supported.',
      }));
      return;
    }
  
    // Validate email using regex
    const emailRegex = /^[a-zA-Z0-9._-]+@gmail\.com$/; // Ensure only Gmail is allowed
    const isValid = emailRegex.test(email);
  
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      email: isValid ? '' : 'Email is invalid.',
    }));
  };
  
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value); // Update password state
  
    // Skip validation if either password or confirmpassword is empty
    if (value === "" || confirmpassword === "") {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        confirmpassword: "", // Clear confirmpassword error if fields are empty
      }));
    } else {
      // Check if confirm password matches the new password
      if (confirmpassword !== value) {
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          confirmpassword: "Passwords do not match",
        }));
      } else {
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          confirmpassword: "",
        }));
      }
    }
  
    // Clear password error message if input value changes
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      password: "",
    }));
  };
  
  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value); // Update confirm password state
  
    // Skip validation if either password or confirmpassword is empty
    if (password === "" || value === "") {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        confirmpassword: "", // Clear confirmpassword error if fields are empty
      }));
    } else {
      // Check if confirm password matches the password
      if (password !== value) {
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          confirmpassword: "Passwords do not match",
        }));
      } else {
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          confirmpassword: "",
        }));
      }
    }
  
    // Clear other error messages if input value changes
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      name: prevErrors.name ? "" : prevErrors.name,
      email: prevErrors.email ? "" : prevErrors.email,
      password: prevErrors.password ? "" : prevErrors.password,
    }));
  };

  return (
    <Flex
      minH={'100vh'}
      justify={'center'}      
      bg={bgColor}
      color={textColor}
      width="100%"
      pt={`calc(1rem + env(safe-area-inset-top))`}
      >
        <Stack
          w={'full'}
          maxW={'lg'}
          //bg={bgColorDark}
          p={8}
          >
          <Image 
            src="/logo512.png" 
            alt="iFun Baby Logo" 
            boxSize="150px" 
            mx="auto" 
            mt={7}
          />
          <Text display='flex' justifyContent='center' alignItems='center' fontSize={{ base: 'md', sm: 'lg' }} mb="4">
            Please provide your details to register.
          </Text>
       
          {/* <form onSubmit={submitHandler}> */}

            <FormControl isRequired isInvalid={!!formErrors?.name}>
              <InputGroup>
                <InputLeftElement>
                  <FontAwesomeIcon
                    color='gray'
                    icon={faUser}
                  />
                </InputLeftElement>
                <Input 
                  id="name"
                  type="text" 
                  value={name}
                  placeholder='Display name'
                  autoFocus
                  autoComplete='false'
                  onChange={handleNameChange} 
                  />
              </InputGroup>
              <FormErrorMessage>{formErrors?.name}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!formErrors?.email}>
              <InputGroup>
                <InputLeftElement>
                  <FontAwesomeIcon
                    color='gray'
                    icon={faAt}
                  />
                </InputLeftElement>
                <Input 
                  id="email"
                  type="email" 
                  value={email}
                  placeholder='Email address'
                  autoComplete='false'
                  onChange={handleEmailChange} 
                  onBlur={handleEmailBlur}
                  />
              </InputGroup>
              <FormErrorMessage>{formErrors?.email}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!formErrors?.password}>
                <PasswordField placeholder='Password' onChange={handlePasswordChange} />
                <FormErrorMessage>{formErrors?.password}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!formErrors?.confirmpassword}>
                <PasswordField placeholder='Confirm password'  onChange={handleConfirmPasswordChange} />
                <FormErrorMessage>{formErrors?.confirmpassword}</FormErrorMessage>
            </FormControl>

            <Stack
              direction={{ base: 'column', sm: 'row' }}
              align={'start'}
              justify={'space-between'}
            >
              <FormControl isRequired isInvalid={!!formErrors?.otp}>
                <Input
                  id="otp"
                  variant="outline"
                  type="text"
                  value={otp}
                  onChange={handleOTPChange}
                  placeholder="# Code"
                  width={{ base: '100%', sm: '95%' }}
                />
                <FormErrorMessage>{formErrors?.otp}</FormErrorMessage>
              </FormControl>

              <Button
                variant={'outline'}
                onClick={sendOTP}
                colorScheme="teal"
                width={{ base: '100%', sm: '40%' }}
                isDisabled={resendDisabled}
              >
                {resendDisabled ? `Resend in ${formatTime(resendTimer)}` : "Send code"}
              </Button>
            </Stack>

            <Stack spacing={4} pt={2}>
              <Button
                // type="submit"
                loadingText="Submitting"
                colorScheme='teal'
                onClick={submitHandler} 
                isLoading={isLoading}>
                Register
              </Button>
            </Stack>
            <Stack
              direction="row"           
              width="100%"              
              alignItems={'flex-start'}
              //justifyContent={'space-between'}
              flexWrap="nowrap"
              >
              <Text>
                Already a user?
              </Text>
              <Link color="#6C8EB3" href='/login'>
                Log in
              </Link>
          </Stack>
        {/* </form> */}
      </Stack>
    </Flex>
  )
}


// 'use client'

// import {
//   Flex,
//   FormControl,
//   FormErrorMessage,
//   Input,
//   Stack,
//   Button,
//   InputGroup,
//   InputLeftElement,
//   Text,
//   useColorModeValue,
//   Link,
//   Image,
// } from '@chakra-ui/react'
// import { PasswordField } from '../../components/PasswordField';
// import { ToastService } from '../../services/toast/toastService';
// import { useState, useEffect } from "react";
// import { useNavigate } from "react-router";
// import { useDispatch, useSelector } from "react-redux";
// import { useRegisterMutation } from "../../slices/userApiSlice";
// import { setCredentials } from "../../slices/authSlice";
// import { useUserSendEmailOtpMutation, useUserVerifyEmailMutation } from "../../slices/userApiSlice";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faAt, faUser } from "@fortawesome/free-solid-svg-icons"; 

// export default function UserRegister() {
//   const [name, setName] = useState('');
//   const [email, setEmail] = useState('');
//   const [confirmpassword, setConfirmPassword] = useState('');
//   const [password, setPassword] = useState('');
//   const {success, error} = ToastService();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const bgColor = useColorModeValue('custom.900', 'gray.800');
//   const bgColorDark = useColorModeValue('gray.50', 'gray.700');
//   const textColor = useColorModeValue('custom.800', 'custom.900');
//   const { userInfo } = useSelector( (state) => state.auth );
//   const [register, { isLoading }] = useRegisterMutation();
//   const [formErrors, setFormErrors] = useState();

//   const [otp, setOTP] = useState(""); 
//   // const [errorMessage, setFormErrors] = useState(""); 
//   const [resendDisabled, setResendDisabled] = useState(false); 
//   const [resendTimer, setResendTimer] = useState(5 * 60); // 10 minutes timer
//   const [expirationTime, setExpirationTime] = useState(null); // Store expiration time
//   const [sendEmailOtp] = useUserSendEmailOtpMutation(); // Send OTP hook
//   const [verifyEmail] = useUserVerifyEmailMutation(); // Verify OTP hook
//   //const [otpTime, setOtpTime] = useState(0); // Time in seconds

//   useEffect( () => {
//     if(userInfo) {
//       navigate('/');
//     }
//   }, [ navigate, userInfo ] );

//   const validateForm = (excludeOTP = false) => {
//     let errors = {};
//     if (!name) {
//       errors.name = "Please enter your display name.";
//     }
//     if (!email) {
//       errors.email = "Please enter your email address.";
//     }
//     if (!password) {
//       errors.password = "Please enter your password.";
//     }
//     if (!confirmpassword) {
//       errors.confirmpassword = "Please confirm your password.";
//     }
//     if (password && confirmpassword && password !== confirmpassword) {
//       errors.confirmpassword = "Your passwords don't match. Please try again.";
//     }
//     if (!excludeOTP && !otp) {
//       errors.otp = "Verification code is required.";
//     }
//     return errors;
//   };

//   const sendOTP = async (e) => {
//     // Validate form
//     const validationErrors = validateForm(true);
//     if (Object.keys(validationErrors).length > 0) {
//       setFormErrors((prevErrors) => ({
//         ...prevErrors,
//         ...validationErrors,
//       }));
//       return;
//     }
  
//     try {
//       const response = await sendEmailOtp({ email }).unwrap();
  
//       if (response.success) {
//         // Set new expiration time (5 minutes from now)
//         const newExpirationTime = Date.now() + 5 * 60 * 1000; // 5 minutes in milliseconds
//         setExpirationTime(newExpirationTime);
  
//         // Reset resend timer and disable the resend button
//         setResendDisabled(true);
//         setResendTimer(5 * 60); // Reset timer to 5 minutes
  
//         success('A verification code has been sent to your email.');
//       } else {
//         error('Please check your email address');
//       }
//     } catch (err) {
//       error(err.data?.message || 'Failed to send OTP. Please try again.');
//     }
//   };

//   useEffect(() => {
//     if (expirationTime) {
//       const interval = setInterval(() => {
//         const remainingTime = Math.max(0, Math.floor((expirationTime - Date.now()) / 1000)); // Calculate remaining time in seconds
//         setResendTimer(remainingTime);

//         if (remainingTime <= 0) {
//           setResendDisabled(false); // Re-enable resend button after OTP expiration
//           clearInterval(interval);
//         }
//       }, 1000);

//       return () => clearInterval(interval); // Cleanup interval on component unmount
//     }
//   }, [expirationTime]);

//   const formatTime = (timeInSeconds) => {
//     const minutes = Math.floor(timeInSeconds / 60); // Get minutes
//     const seconds = timeInSeconds % 60; // Get remaining seconds
//     return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`; // Format as MM:SS
//   };

//   const submitHandler = async (e) => {
//     e.preventDefault();
  
//     // Step 1: Validate the form
//     const validationErrors = validateForm();
//     if (Object.keys(validationErrors).length > 0) {
//       setFormErrors((prevErrors) => ({
//         ...prevErrors,
//         ...validationErrors,
//       }));
//       return; // Exit if validation fails
//     }
  
//     // Step 2: Check if OTP has expired (only if expirationTime is set)
//     if (expirationTime && Date.now() > expirationTime) {
//       setFormErrors('OTP has expired. Please request a new OTP.');
//       return; // Exit if OTP has expired
//     }
  
//     try {
//       // Step 3: Verify OTP
//       const verifyResponse = await verifyEmail({ email, otp, expirationTime }).unwrap();
//       if (!verifyResponse.success) {
//         setFormErrors(verifyResponse.message || 'Email verification failed');
//         return;
//       }
  
//       // Step 4: If OTP verification is successful, proceed with registration
//       const registerResponse = await register({ name, email, password }).unwrap();
//       const { qrCode, user } = registerResponse;
  
//       // Step 5: Dispatch user credentials to Redux store
//       dispatch(setCredentials({ ...user, qrCode }));
  
//       // Step 6: Clear form after successful registration
//       setName('');
//       setEmail('');
//       setPassword('');
//       setConfirmPassword('');
  
//       navigate('/'); // Navigate to the home page after registration
//       success('Registration successful!');
//     } catch (err) {
//       // Handle any errors that occur during verification or registration
//       const errorMessage = err?.data?.message || 'An unexpected error occurred.';
//       setFormErrors(errorMessage);
//       error(errorMessage);
//     }
//   };

//   // OTP input change handler
//   const handleOTPChange = (e) => {
//     const value = e.target.value;
  
//     // Update name state
//     setOTP(value);
  
//     // Clear error message if input value changes
//     setFormErrors((prevErrors) => ({
//       ...prevErrors,
//       otp: "", // Clear the error for the name field
//     }));

//   };

//   const handleNameChange = (e) => {
//     const value = e.target.value;
  
//     // Update name state
//     setName(value);
  
//     // Clear error message if input value changes
//     setFormErrors((prevErrors) => ({
//       ...prevErrors,
//       name: "", // Clear the error for the name field
//     }));
//   };
  

//   const handleEmailChange = (e) => {
//     const value = e.target.value;
//     setEmail(value);
//   };  

//   const handleEmailBlur = () => {
//     if (!email) return; // If email is empty, do nothing
  
//     // Email domain check (Only allow Gmail)
//     const emailDomain = email.split('@')[1];
//     if (emailDomain && emailDomain !== 'gmail.com') {
//       setFormErrors((prevErrors) => ({
//         ...prevErrors,
//         email: 'Only Gmail addresses are supported.',
//       }));
//       return;
//     }
  
//     // Validate email using regex
//     const emailRegex = /^[a-zA-Z0-9._-]+@gmail\.com$/; // Ensure only Gmail is allowed
//     const isValid = emailRegex.test(email);
  
//     setFormErrors((prevErrors) => ({
//       ...prevErrors,
//       email: isValid ? '' : 'Email is invalid.',
//     }));
//   };
  
//   const handlePasswordChange = (e) => {
//     const value = e.target.value;
//     setPassword(value); // Update password state
  
//     // Skip validation if either password or confirmpassword is empty
//     if (value === "" || confirmpassword === "") {
//       setFormErrors((prevErrors) => ({
//         ...prevErrors,
//         confirmpassword: "", // Clear confirmpassword error if fields are empty
//       }));
//     } else {
//       // Check if confirm password matches the new password
//       if (confirmpassword !== value) {
//         setFormErrors((prevErrors) => ({
//           ...prevErrors,
//           confirmpassword: "Passwords do not match",
//         }));
//       } else {
//         setFormErrors((prevErrors) => ({
//           ...prevErrors,
//           confirmpassword: "",
//         }));
//       }
//     }
  
//     // Clear password error message if input value changes
//     setFormErrors((prevErrors) => ({
//       ...prevErrors,
//       password: "",
//     }));
//   };
  
//   const handleConfirmPasswordChange = (e) => {
//     const value = e.target.value;
//     setConfirmPassword(value); // Update confirm password state
  
//     // Skip validation if either password or confirmpassword is empty
//     if (password === "" || value === "") {
//       setFormErrors((prevErrors) => ({
//         ...prevErrors,
//         confirmpassword: "", // Clear confirmpassword error if fields are empty
//       }));
//     } else {
//       // Check if confirm password matches the password
//       if (password !== value) {
//         setFormErrors((prevErrors) => ({
//           ...prevErrors,
//           confirmpassword: "Passwords do not match",
//         }));
//       } else {
//         setFormErrors((prevErrors) => ({
//           ...prevErrors,
//           confirmpassword: "",
//         }));
//       }
//     }
  
//     // Clear other error messages if input value changes
//     setFormErrors((prevErrors) => ({
//       ...prevErrors,
//       name: prevErrors.name ? "" : prevErrors.name,
//       email: prevErrors.email ? "" : prevErrors.email,
//       password: prevErrors.password ? "" : prevErrors.password,
//     }));
//   };

//   return (
//     <Flex
//       minH={'100vh'}
//       justify={'center'}      
//       bg={bgColor}
//       color={textColor}
//       width="100%">
//       <Stack
//           w={'full'}
//           maxW={'lg'}
//           bg={bgColorDark}
//           p={8}
//           >
//           <Image 
//             src="/logo512.png" 
//             alt="iFun Baby Logo" 
//             boxSize="150px" 
//             mx="auto" 
//             mt={7}
//           />
//           <Text display='flex' justifyContent='center' alignItems='center' fontSize={{ base: 'md', sm: 'lg' }} mb="4">
//             Please provide your details to register.
//           </Text>
       
//             <FormControl isRequired isInvalid={!!formErrors?.name}>
//               <InputGroup>
//                 <InputLeftElement>
//                   <FontAwesomeIcon
//                     color='gray'
//                     icon={faUser}
//                   />
//                 </InputLeftElement>
//                 <Input 
//                   id="name"
//                   type="text" 
//                   value={name}
//                   placeholder='Display name'
//                   autoFocus
//                   autoComplete='false'
//                   onChange={handleNameChange} 
//                   />
//               </InputGroup>
//               <FormErrorMessage>{formErrors?.name}</FormErrorMessage>
//             </FormControl>

//             <FormControl isRequired isInvalid={!!formErrors?.email}>
//               <InputGroup>
//                 <InputLeftElement>
//                   <FontAwesomeIcon
//                     color='gray'
//                     icon={faAt}
//                   />
//                 </InputLeftElement>
//                 <Input 
//                   id="email"
//                   type="email" 
//                   value={email}
//                   placeholder='Email address'
//                   autoComplete='false'
//                   onChange={handleEmailChange} 
//                   onBlur={handleEmailBlur}
//                   />
//               </InputGroup>
//               <FormErrorMessage>{formErrors?.email}</FormErrorMessage>
//             </FormControl>

//             <FormControl isRequired isInvalid={!!formErrors?.password}>
//                 <PasswordField placeholder='Password' onChange={handlePasswordChange} />
//                 <FormErrorMessage>{formErrors?.password}</FormErrorMessage>
//             </FormControl>

//             <FormControl isRequired isInvalid={!!formErrors?.confirmpassword}>
//                 <PasswordField placeholder='Confirm password'  onChange={handleConfirmPasswordChange} />
//                 <FormErrorMessage>{formErrors?.confirmpassword}</FormErrorMessage>
//             </FormControl>

//             <Stack
//               direction={{ base: 'column', sm: 'row' }}
//               align={'start'}
//               justify={'space-between'}
//             >
//               <FormControl isRequired isInvalid={!!formErrors?.otp}>
//                 <Input
//                   id="otp"
//                   variant="outline"
//                   type="text"
//                   value={otp}
//                   onChange={handleOTPChange}
//                   placeholder="# Code"
//                   width={{ base: '100%', sm: '95%' }}
//                 />
//                 <FormErrorMessage>{formErrors?.otp}</FormErrorMessage>
//               </FormControl>

//               <Button
//                 variant={'outline'}
//                 onClick={sendOTP}
//                 colorScheme="teal"
//                 width={{ base: '100%', sm: '40%' }}
//                 isDisabled={resendDisabled}
//               >
//                 {resendDisabled ? `Resend in ${formatTime(resendTimer)}` : "Send code"}
//               </Button>
//             </Stack>

//             <Stack spacing={10} pt={2}>
//               <Button
//                 loadingText="Submitting"
//                 colorScheme='teal'
//                 onClick={submitHandler} 
//                 isLoading={isLoading}>
//                 Register
//               </Button>
//             </Stack>
//             <Stack
//               direction="row"           
//               width="100%"              
//               alignItems={'flex-start'}
//               justifyContent={'space-between'}
//               flexWrap="nowrap"
//               >
//               <Text>
//                 Already a user?
//               </Text>
//               <Link color="#6C8EB3" href='/login'>
//                 Log in
//               </Link>
//             </Stack>
//           </Stack>
//     </Flex>
//   )
// }